import { ExpectedError } from "../../expectedError";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../winrm/logo.svg";

function endpoint({ host, port, username }: Record<string, string>) {
  const hostname = (host ?? "").trim();
  if (
    !/^[A-Za-z0-9.-]+$/.test(hostname) &&
    !/^\[[0-9a-fA-F:]+\]$/.test(hostname)
  ) {
    throw new ExpectedError("Invalid SSH host");
  }
  const value = port || "22";
  if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 65535) {
    throw new ExpectedError("Invalid SSH port");
  }
  if (!username?.trim()) throw new ExpectedError("SSH username is required");
  return new URL(`ssh://${encodeURIComponent(username)}@${hostname}:${value}`)
    .href;
}

const usage =
  "Use any SSH or SFTP client available in the workflow environment. Connect to SSH_HOST and SSH_PORT with username SSH_USER and no password, private key, or agent (SSH none authentication). The proxy verifies the server’s configured host key and authenticates with the stored private key. For this connector connection only, accept the proxy’s ephemeral host key without persisting it. Commands, interactive shells, SFTP file transfers, PTYs, and terminal resizing are supported. Multiple sessions can share a connection, with up to 10 sessions open at once. Close the connection when finished. Environment, agent, X11, and port forwarding requests are not supported.";

export const ssh = defineConnector({
  identifier: "ssh",
  name: "SSH",
  tagLine: "Run commands and transfer files over SSH",
  keywords: "ssh sftp secure shell remote command server file transfer",
  logo,
  tags: ["it", "custom"],
  protocol: "ssh",
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("ssh"),
  aiContext: usage,
  connectionTest: { protocol: "ssh", connectionString: endpoint },
  steps: [
    {
      title: "Enter your SSH connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "SSH",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "hostname",
          label: "Host",
          required: true,
          placeholder: "server.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "22",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "file",
          label: "Private key",
          accept: ".pem,.key,.txt",
          required: true,
          helperText:
            "Upload an unencrypted OpenSSH or PEM private key. RSA keys must be at least 2048 bits, and the server must support RSA SHA-2 signatures. The key stays in the proxy and is never exposed to workflow code.",
          target: { kind: "secrets", field: "privateKey" },
        },
        {
          kind: "input",
          label: "Server host public key",
          required: true,
          placeholder: "ssh-ed25519 AAAA…",
          helperText:
            "Get the server’s public host key from its administrator over a trusted channel. Paste the key type and base64 key, without a hostname. Connections fail if the server presents a different key.",
          target: { kind: "configuration", field: "hostKey" },
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => {
    const url = new URL(endpoint(configuration));
    return {
      SSH_HOST: url.hostname.replace(/^\[|\]$/g, ""),
      SSH_PORT: url.port,
      SSH_USER: decodeURIComponent(url.username),
    };
  },
  usageHint: () => usage,
});
